import React, { useState } from 'react';
import { Box, Button, FormControlLabel, IconButton, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Slider, Stack, Switch, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import StarIcon from '@mui/icons-material/StarOutline';
import type { BrandAsset, BrandKit } from '../../../services/editingApi';
import type { EditList, Overlay } from '../../../utils/editing/editList';
import { EDIT_LIMITS, outputDurationMs } from '../../../utils/editing/editList';
import { addOverlay, defaultPosition, formatMs, removeItem, updateItem } from '../editStudioModel';
import { PanelHint, PositionPicker, SecondsField } from './controls';

interface PicturesPanelProps {
  list: EditList;
  kit: BrandKit | null;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: EditList, key?: string) => void;
  /** Opens the brand kit; what's uploaded there is added to the edit at once. */
  onUpload: () => void;
  onOpenBrandKit: () => void;
}

/** A picture as it is, on a checkerboard so see-through parts show (a wide logo stays readable). */
const Thumb: React.FC<{ url?: string | null }> = ({ url }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 72, height: 40, flex: '0 0 auto', mr: 1.25, borderRadius: 1, overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid', borderColor: 'divider',
      backgroundColor: '#fff',
      backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
      backgroundSize: '10px 10px',
      backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0',
    }}
  >
    {url ? <img src={url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} /> : null}
  </Box>
);

/** Your logo (one click) and any picture from the brand kit, placed by drag or by the grid. */
const PicturesPanel: React.FC<PicturesPanelProps> = ({ list, kit, selectedId, onSelect, onChange, onUpload, onOpenBrandKit }) => {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const total = outputDurationMs(list);
  const images: BrandAsset[] = (kit?.assets || []).filter((asset) => asset.kind === 'image');
  const logo = kit?.logoAssetId ? images.find((asset) => asset.id === kit.logoAssetId) || null : null;
  const logoOn = Boolean(logo && list.overlays.some((overlay) => overlay.assetId === logo.id));
  const overlay = list.overlays.find((item) => item.id === selectedId) || null;
  const assetName = (id: string) => images.find((asset) => asset.id === id)?.name || 'Picture';
  const changeOverlay = (change: Partial<Overlay>, key?: string) => { if (overlay) onChange({ ...list, overlays: updateItem(list.overlays, overlay.id, change) }, key); };
  const addPicture = (asset: BrandAsset) => {
    const isLogo = kit?.logoAssetId === asset.id;
    const next = addOverlay(list, asset.id, defaultPosition(isLogo ? 'top-right' : 'center', isLogo ? 3 : 0));
    onChange(next);
    onSelect(next.overlays[next.overlays.length - 1]?.id ?? null);
    setMenu(null);
  };
  const full = Boolean(overlay && overlay.fromMs === null && overlay.toMs === null);

  return (
    <Box>
      <PanelHint>Pictures from your brand kit over the video. Drag one on the picture to place it; drag its corner to resize.</PanelHint>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {logo && !logoOn ? (
          <Button size="small" variant="contained" startIcon={<StarIcon />} onClick={() => addPicture(logo)} data-testid="add-logo">Add my logo</Button>
        ) : null}
        {!logo ? (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onUpload} data-testid="upload-logo">Upload your logo</Button>
        ) : null}
        <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={list.overlays.length >= EDIT_LIMITS.maxOverlays} onClick={(event) => (images.length ? setMenu(event.currentTarget) : onUpload())} data-testid="add-picture">
          {images.length ? 'Add a picture' : 'Upload a picture'}
        </Button>
        <Button size="small" onClick={onOpenBrandKit}>Brand kit</Button>
      </Stack>
      <Menu open={Boolean(menu)} anchorEl={menu} onClose={() => setMenu(null)}>
        {images.map((asset) => (
          <MenuItem key={asset.id} onClick={() => addPicture(asset)}>
            <Thumb url={asset.url} />
            {asset.name}{kit?.logoAssetId === asset.id ? ' (logo)' : ''}
          </MenuItem>
        ))}
        <MenuItem onClick={() => { setMenu(null); onUpload(); }}>Upload another…</MenuItem>
      </Menu>
      {list.overlays.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing on the picture yet. {logo ? 'Add your logo with one click.' : 'Upload your logo to brand every video.'}</Typography>
      ) : (
        <List dense disablePadding>
          {list.overlays.map((item) => (
            <ListItem key={item.id} disableGutters secondaryAction={<IconButton size="small" aria-label={`Remove ${assetName(item.assetId)}`} onClick={() => { onChange({ ...list, overlays: removeItem(list.overlays, item.id) }); if (selectedId === item.id) onSelect(null); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>}>
              <ListItemButton selected={item.id === selectedId} onClick={() => onSelect(item.id)} sx={{ borderRadius: 1 }} data-testid={`picture-item-${item.id}`}>
                <Thumb url={images.find((asset) => asset.id === item.assetId)?.url} />
                <ListItemText primary={assetName(item.assetId)} secondary={item.fromMs === null && item.toMs === null ? 'Whole video' : `${formatMs(item.fromMs ?? 0)} – ${formatMs(item.toMs ?? total)}`} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      {overlay ? (
        <Stack spacing={1.5} sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} data-testid="picture-settings">
          <PositionPicker value={overlay.position} onChange={(position) => changeOverlay({ position })} />
          <Box>
            <Typography variant="caption" color="text.secondary">Size: {Math.round(overlay.widthPct)}% of the width</Typography>
            <Slider size="small" min={EDIT_LIMITS.overlayWidthPct.min} max={60} step={1} value={overlay.widthPct} onChange={(_, v) => changeOverlay({ widthPct: v as number }, `width-${overlay.id}`)} aria-label="Picture size" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Opacity: {Math.round(overlay.opacity * 100)}%</Typography>
            <Slider size="small" min={0.1} max={1} step={0.05} value={overlay.opacity} onChange={(_, v) => changeOverlay({ opacity: v as number }, `opacity-${overlay.id}`)} aria-label="Picture opacity" />
          </Box>
          <FormControlLabel control={<Switch size="small" checked={full} onChange={(event) => changeOverlay(event.target.checked ? { fromMs: null, toMs: null } : { fromMs: 0, toMs: Math.min(total, 5000) })} />} label={<Typography variant="body2">The whole video</Typography>} />
          {!full ? (
            <Stack direction="row" spacing={1}>
              <SecondsField label="From" valueMs={overlay.fromMs} allowEmpty maxMs={total} onChange={(fromMs) => changeOverlay({ fromMs, toMs: overlay.toMs !== null && fromMs !== null && overlay.toMs <= fromMs ? null : overlay.toMs })} />
              <SecondsField label="To" valueMs={overlay.toMs} allowEmpty maxMs={total} onChange={(toMs) => changeOverlay({ toMs: toMs !== null && overlay.fromMs !== null && toMs <= overlay.fromMs ? null : toMs })} />
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default PicturesPanel;
